// CPT Category and Modifier Filtering Types

export type CptCategory = 'SURGICAL' | 'IMAGING' | 'OFFICE_PROC' | 'EVAL_MGMT' | 'UNKNOWN';

export interface CptTagMapEntry {
  cpt_code: string;
  category: CptCategory;
  laterality_required: boolean;
  allows_assistant: boolean;
  allows_26_tc: boolean;
  notes_internal?: string;
}

export type SiteOfService = 'hospital' | 'asc' | 'office';
export type Laterality = 'left' | 'right' | 'bilateral';

// Laterality modifiers
export const LATERALITY_MODIFIERS = ['RT', 'LT', '50'] as const;
export type LateralityModifier = typeof LATERALITY_MODIFIERS[number];

// Base surgeon-visible modifier list
export const BASE_SURGEON_MODIFIERS: string[] = [
  'RT', 'LT', '50', '51', '52', '53', 'XE', 'XS', 'XP', 'XU', '59', '76', '77', '78', '79', '80', 'AS', '22', '24', '25', '26', 'TC'
];

// Category-specific modifier allowlists (typed as string[] for flexibility)
export const IMAGING_MODIFIERS: string[] = ['26', 'TC', 'XE', 'XS', 'XP', 'XU', '59'];
export const EVAL_MGMT_MODIFIERS: string[] = ['24', '25', 'XE', 'XS', 'XP', 'XU', '59'];
export const UNKNOWN_MODIFIERS: string[] = ['RT', 'LT', '50', '52', '53', 'XE', 'XS', 'XP', 'XU', '59', '25'];

export const SURGICAL_OFFICE_MODIFIERS: string[] = ['25', 'XE', 'XS', 'XP', 'XU', '59', '52', '53'];
export const SURGICAL_FACILITY_MODIFIERS: string[] = [
  'RT', 'LT', '50', '51', '52', '53', 'XE', 'XS', 'XP', 'XU', '59', '76', '77', '78', '79', '80', 'AS', '22', '24', '25'
];

// Modifier descriptions for UI
export const MODIFIER_DESCRIPTIONS: Record<string, string> = {
  // Unbundling / Revenue Protectors
  '59': 'Distinct Procedural Service — legacy fallback; prefer XE/XS/XP/XU when supported',
  '51': 'Multiple Procedures — multiple primary procedures in same session',
  'XE': 'Separate Encounter (CMS alternative to -59)',
  'XU': 'Unusual Non-Overlapping Service (CMS alternative to -59)',
  'XS': 'Separate Structure (CMS alternative to -59)',
  'XP': 'Separate Practitioner (CMS alternative to -59)',
  // Laterality (Mandatory for Ortho)
  'RT': 'Right side',
  'LT': 'Left side',
  '50': 'Bilateral procedure',
  // Ancillary / Surgeon
  'AS': 'Physician Assistant / NP assisting in surgery',
  '80': 'Assistant Surgeon (MD/DO)',
  '22': 'Increased Procedural Services — unusually complex (e.g., morbid obesity, severe scar tissue)',
  // Other standard modifiers
  '52': 'Reduced services',
  '53': 'Discontinued procedure',
  '76': 'Repeat procedure by same physician',
  '77': 'Repeat procedure by another physician',
  '78': 'Unplanned return to OR',
  '79': 'Unrelated procedure during postop period',
  '24': 'Unrelated E/M during postop period',
  '25': 'Significant, separate E/M service',
  '26': 'Professional component',
  'TC': 'Technical component',
};

export interface ModifierFilterResult {
  allowedModifiers: string[];
  lateralityRequired: boolean;
  lateralityModifiers: string[];
  nonLateralityModifiers: string[];
  showLateralityToggle: boolean;
  modifierReviewRequired: boolean;
  validationMessage?: string;
}

export interface ModifierValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  modifierReviewRequired: boolean;
}
